import { useState, useEffect, useRef, type ReactNode } from 'react';
import type { CountingQuestionType } from '../../types/counting';
import { SuitCountInput } from './SuitCountInput';
import { HandShapeInput } from './HandShapeInput';

/**
 * The counting question and its answer pad (feature 028).
 *
 * Sits in the table frame's `call` slot, so it renders the question and the pad only — the panel
 * chrome around it belongs to the frame.
 *
 * **The reveal rule is unchanged**: until the play reaches the stop trick the question is
 * obscured and non-interactive, so a learner cannot read ahead. Two things about how that is
 * done here differ from the pre-028 pad, and both are requirements rather than taste:
 *
 * 1. **A non-motion carrier.** The old pad announced the reveal with a five-second border blink.
 *    Under `prefers-reduced-motion` that animation is suppressed, which left those learners with
 *    no signal at all that the question had become answerable. The heading now states it in words
 *    ("Question at trick #N"), the blur lifts either way, and the blink is an enhancement on top
 *    for learners who accept motion.
 * 2. **Hidden, not just blurred.** A CSS blur is a visual effect: the text stays in the
 *    accessibility tree and a screen reader would read the question out before the learner was
 *    meant to have it. The obscured question is now `aria-hidden` and the pad is removed from the
 *    tab order until the stop trick is reached, so the rule holds for everyone.
 */

interface CountingQuestionProps {
  questionType: CountingQuestionType;
  questionText: string;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  /** Whether the animation has reached the stop trick at least once. */
  hasReachedStopTrick: boolean;
  /** The trick number at which the question is asked (1-based). */
  stopAfterTrick: number;
  /**
   * The page's second action — "Skip Hand" — which belongs in the same row as Submit rather than
   * under it (client report, 2026-09-03: stacked, the two full-width buttons took ~52px out of
   * the table above and left the auction one bid row on a 384x698 phone). It travels down into
   * the pad because only a shared parent can put them side by side; before the question is
   * revealed there is no Submit, so it stands alone in the same row.
   *
   * Optional: without it this renders exactly what it rendered before.
   */
  secondaryAction?: ReactNode;
}

export function CountingQuestion({
  questionType,
  questionText,
  onSubmit,
  disabled,
  hasReachedStopTrick,
  stopAfterTrick,
  secondaryAction,
}: CountingQuestionProps) {
  const [isBlinking, setIsBlinking] = useState(false);
  const hasBlinkFiredRef = useRef(false);

  useEffect(() => {
    if (!hasReachedStopTrick || hasBlinkFiredRef.current) return;
    hasBlinkFiredRef.current = true;

    // Motion is an enhancement here, never the message — the heading below already says the
    // question is live. Suppressed entirely for learners who asked for reduced motion.
    const prefersReducedMotion =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    if (prefersReducedMotion) return;

    setIsBlinking(true);
    const timer = setTimeout(() => setIsBlinking(false), 5000);
    return () => clearTimeout(timer);
  }, [hasReachedStopTrick]);

  // A new hand re-arms the reveal.
  useEffect(() => {
    hasBlinkFiredRef.current = false;
    setIsBlinking(false);
  }, [questionText]);

  return (
    <div className="space-y-3 xnarrow:space-y-2 padswap:space-y-2" data-testid="counting-question">
      <h2
        className={`text-[13px] font-semibold tracking-wide text-ink-soft ${
          isBlinking ? 'counting-question-live' : ''
        }`}
        data-testid="counting-question-heading"
      >
        {hasReachedStopTrick ? `Question at trick #${stopAfterTrick}` : 'Question'}
      </h2>

      {hasReachedStopTrick ? (
        <>
          <p className="text-[15px] font-semibold text-ink" data-testid="counting-question-text">
            {questionText}
          </p>
          {questionType === 'hand-shape' ? (
            <HandShapeInput onSubmit={onSubmit} disabled={disabled} trailingAction={secondaryAction} />
          ) : (
            <SuitCountInput onSubmit={onSubmit} disabled={disabled} trailingAction={secondaryAction} />
          )}
        </>
      ) : (
        // Held back until the play reaches the stop trick. Only the QUESTION is hidden from
        // assistive technology — the instruction below stays announced, so a screen-reader user
        // learns why the pad is not there instead of meeting silence. The block keeps its height
        // so the panel does not jump when the question arrives.
        <div data-testid="counting-question-withheld">
          <p
            aria-hidden="true"
            className="select-none text-[15px] font-semibold text-ink blur-[5px]"
          >
            {questionText}
          </p>
          <p className="mt-3 text-[12px] text-ink-soft">
            Play on to trick {stopAfterTrick} to see the question.
          </p>
          {/* The same row Submit will occupy, so the panel's height does not jump by a button
              when the question arrives — it is the row's second slot either way. */}
          {secondaryAction && <div className="counting-answer-actions mt-3">{secondaryAction}</div>}
        </div>
      )}
    </div>
  );
}
